import { memo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { MoreVertical } from 'lucide-react';

import crLogo from '@/assets/CR.png';

import { useAudio, useAuth, useConfig } from '@chainlit/react-client';

import AudioPresence from '@/components/AudioPresence';
import ButtonLink from '@/components/ButtonLink';
import { useSidebar } from '@/components/ui/sidebar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';

import { cn } from '@/lib/utils';
import { useModelName } from '@/hooks/useModelName';
import { useIsMobile } from '@/hooks/use-mobile';

import ApiKeys from './ApiKeys';
import ChatProfiles from './ChatProfiles';
import { ModelName } from './ModelName';
import NewChatButton from './NewChat';
import OperationsButton from './OperationsButton';
import ReadmeButton from './Readme';
import ShareButton from './Share';
import LogoSidebarToggle from './LogoSidebarToggle';
import TokenUsageIndicator from './TokenUsageIndicator';
import UserNav from './UserNav';
import LayoutControls from './LayoutControls';
import LayoutPresetPicker from './LayoutPresetPicker';
import ThreadOperationsIndicator from '@/components/operations/ThreadOperationsIndicator';

const Header = memo(() => {
  const { audioConnection } = useAudio();
  const navigate = useNavigate();
  const { data } = useAuth();
  const { config } = useConfig();
  const { open, openMobile, isMobile: isSidebarMobile } = useSidebar();
  const { modelName, availableModels, isLoading, updateModelName } = useModelName();
  const isMobile = useIsMobile();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const sidebarOpen = isSidebarMobile ? openMobile : open;

  const historyEnabled = data?.requireLogin && config?.dataPersistence;
  const sidebarHidden = config?.ui?.default_sidebar_state === 'hidden';
  const hasSidebar = historyEnabled && !sidebarHidden;

  // The desktop sidebar rail always shows a new-thread button, so the header
  // only needs one when there's no rail: on mobile (Sheet, hidden while open)
  // or when the sidebar is disabled entirely.
  const showHeaderNewChat = !hasSidebar || (isMobile && !sidebarOpen);

  const links = config?.ui?.header_links || [];

  const appName = config?.ui?.name || 'Crimson Raven';

  return (
    <div className="relative" id="header">
      <div
        className={cn(
          'relative flex h-[64px] items-center justify-between gap-4 px-4',
          'bg-gradient-to-b from-card to-card/70 backdrop-blur-xl',
          'border-b border-border/80',
          "before:absolute before:inset-0 before:pointer-events-none before:opacity-[0.035] before:bg-[radial-gradient(circle_at_1px_1px,_hsl(var(--primary))_1px,_transparent_0)] before:bg-[length:16px_16px]"
        )}
      >
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex items-center gap-2.5 pr-2">
            {isMobile ? (
              <LogoSidebarToggle />
            ) : (
              <img
                src={crLogo}
                alt="Crimson Raven"
                className="h-8 w-8 shrink-0 rounded-full object-cover ring-1 ring-border/60"
              />
            )}
            <div className="hidden md:flex flex-col leading-none">
              <span className="text-[11px] font-bold tracking-[0.18em] uppercase text-foreground">
                {appName}
              </span>
              <span className="mt-0.5 text-[9px] font-medium tracking-[0.25em] uppercase text-muted-foreground">
                Command Center
              </span>
            </div>
          </div>
          <div className="h-8 w-px bg-gradient-to-b from-transparent via-border to-transparent" aria-hidden="true" />
          <div className="flex items-center gap-1">
            {showHeaderNewChat ? <NewChatButton navigate={navigate} /> : null}
            {isMobile && (
              <DropdownMenu open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-9 touch-manipulation"
                    aria-label="More options"
                  >
                    <MoreVertical className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56">
                  <div className="flex flex-col gap-1 p-2">
                    <ModelName
                      modelName={modelName}
                      availableModels={availableModels}
                      isLoading={isLoading}
                      onModelChange={updateModelName}
                    />
                    <ChatProfiles navigate={navigate} />
                    <DropdownMenuSeparator />
                    <ShareButton />
                    <ReadmeButton />
                    <ApiKeys />
                    {links &&
                      links.map((link, index) => (
                        <ButtonLink
                          key={`${link.name}-${link.url}-${index}`}
                          name={link.name}
                          displayName={link.display_name}
                          iconUrl={link.icon_url}
                          url={link.url}
                          target={link.target}
                        />
                      ))}
                  </div>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
          {!isMobile && (
            <>
              <div className="h-8 w-px bg-gradient-to-b from-transparent via-border to-transparent" aria-hidden="true" />
              <div className="flex items-center gap-1.5">
                <ModelName
                  modelName={modelName}
                  availableModels={availableModels}
                  isLoading={isLoading}
                  onModelChange={updateModelName}
                />
                <ChatProfiles navigate={navigate} />
                <ThreadOperationsIndicator />
              </div>
            </>
          )}
        </div>

        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 pointer-events-none">
          {audioConnection === 'on' ? (
            <div className="pointer-events-auto flex items-center gap-2 rounded-full border border-primary/30 bg-card/90 px-3 py-1 shadow-[0_0_12px_-2px_hsl(var(--primary)/0.35)] backdrop-blur-sm">
              <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
              <AudioPresence
                type="server"
                height={24}
                width={48}
                barCount={4}
                barSpacing={2}
              />
            </div>
          ) : null}
        </div>

        {isMobile ? (
          <div className="flex items-center gap-1">
            <OperationsButton />
            <TokenUsageIndicator />
            <UserNav />
          </div>
        ) : (
          <div className="flex items-center gap-1.5">
            <div
              className={cn(
                'flex items-center gap-1 rounded-full border border-border/70',
                'bg-muted/40 px-1 py-0.5 shadow-sm'
              )}
            >
              <OperationsButton />
              <LayoutPresetPicker />
              <LayoutControls />
            </div>
            <div className="h-8 w-px bg-gradient-to-b from-transparent via-border to-transparent" aria-hidden="true" />
            <div className="flex items-center gap-1">
              <ShareButton />
              <ReadmeButton />
              <ApiKeys />
              {links &&
                links.map((link, index) => (
                  <ButtonLink
                    key={`${link.name}-${link.url}-${index}`}
                    name={link.name}
                    displayName={link.display_name}
                    iconUrl={link.icon_url}
                    url={link.url}
                    target={link.target}
                  />
                ))}
            </div>
            <div className="h-8 w-px bg-gradient-to-b from-transparent via-border to-transparent" aria-hidden="true" />
            <div className="flex items-center gap-1">
              <TokenUsageIndicator />
              <UserNav />
            </div>
          </div>
        )}
      </div>
      <div
        aria-hidden="true"
        className="h-[2px] w-full bg-gradient-to-r from-primary/0 via-primary/80 to-redteam/70"
      />
    </div>
  );
});

export { Header };
